import React, { FC, ReactElement, useContext, useMemo, useState } from 'react'
import { $debug } from '../../../../../React/Common/Utils/ConsoleUtils'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import { ForgotPasswordLanguage } from '../../../../../../i18n'
import {
    FormProvider,
    useForm,
    UseFormMethods,
    ValidateResult
} from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { ForgotPasswordForm } from '../Types/Types'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { EMAIL_FIELD_NAME, RECAPTCHA_FIELD_NAME } from '../Constants/FieldNames'
import { OutlinedTextInput } from '../../../Atoms/Inputs/OutlinedTextInput'
import { IsStringEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { EMAIL_REGEX } from '../../../../../NonReact/constants/Regexes'
import { ValidationErrors } from '../../../../../React/Common/Components/ValidationErrors/ValidationErrors'
import { FooterActions } from '../Actions/FooterActions'
import { RoundedButton } from '../../../Atoms/RoundedButton/RoundedButton'
import { ON_CHANGE } from '../../../../../React/Common/Constants/HookFormEventNames'
import { LanguageUtils } from '../../../../../React/Common/Utils/LanguageUtils'
import { ReCaptcha, RECAPTCHA_CAPTCHA_TYPE_INVISIBLE, ReCaptchaHandle } from '../../../Atoms/ReCaptcha/ReCaptcha'
import { requestJSON } from '../../../../../NonReact/common/API/REST/RESTClient'
import { FORGOT_CREDENTIALS_URL } from '../../../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../../../NonReact/constants/REST'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import { REASON } from '../Constants/Constants'

export const ForgotPassword: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ForgotPasswordLanguage.NAME)

    const methods: UseFormMethods<ForgotPasswordForm> = useForm<ForgotPasswordForm>({
        defaultValues: {
            reason: 0,
            email: EMPTY_STRING,
            recaptcha: EMPTY_STRING
        },
        mode: ON_CHANGE,
        shouldUnregister: false
    })

    const [reasonValue, setReasonValue]: ReactState<number> = useState<number>(0)
    const [isSuccessful, setIsSuccessful]: ReactState<boolean> = useState<boolean>(false)

    const { handleSubmit, register, setValue, watch, trigger }: UseFormMethods<ForgotPasswordForm> = methods

    const [reCaptchaHandle, setReCaptchaHandle]: ReactState<Nullable<ReCaptchaHandle>> =
        useState<Nullable<ReCaptchaHandle>>(null)
    const isReCaptchaInvisible: boolean = useMemo(
        (): boolean => reCaptchaHandle?.config?.captchaType === RECAPTCHA_CAPTCHA_TYPE_INVISIBLE,
        [reCaptchaHandle]
    )

    const successfulSubmit = (data: ForgotPasswordForm): void => {
        $debug('ForgotPassword', {
            reason: reasonValue,
            email: data.email,
            recaptcha: data.recaptcha
        })

        requestJSON(FORGOT_CREDENTIALS_URL, {
            method: RESTMethods.POST,
            body: {
                emailAddress: data.email,
                recaptchaToken: data.recaptcha,
                reason: reasonValue
            }
        })
            .then((): void => setIsSuccessful(true))
            .catch(displayError)
    }

    const emailValue: string = watch(EMAIL_FIELD_NAME)

    return (
        <div className='forgot-password-container'>
            {isSuccessful ? (
                <>
                    <h1 className='fp-title'>
                        {reasonValue === REASON.FORGOT_PASSWORD
                            ? language.get(ForgotPasswordLanguage.ForgotPassword_Title_PasswordReset)
                            : language.get(ForgotPasswordLanguage.ForgotPassword_Title_UsernameRecovery)}
                    </h1>
                    <div className='fp-recovery-text'>
                        {reasonValue === REASON.FORGOT_PASSWORD
                            ? LanguageUtils.formatLanguageEntry(
                                  language.getInterpolated(
                                      ForgotPasswordLanguage.ForgotPassword_SuccessMessage_PasswordReset,
                                      emailValue
                                  )
                              )
                            : LanguageUtils.formatLanguageEntry(
                                  language.getInterpolated(
                                      ForgotPasswordLanguage.ForgotPassword_SuccessMessage_UsernameRecover,
                                      emailValue
                                  )
                              )}
                    </div>
                </>
            ) : (
                <>
                    <h1 className='fp-title'>{language.get(ForgotPasswordLanguage.ForgotPassword_Title)}</h1>
                    <h2 className='fp-subtitle'>{language.get(ForgotPasswordLanguage.ForgotPassword_WhatDoYouNeed)}</h2>

                    <FormProvider {...methods}>
                        <form
                            className='fp-form-container'
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onSubmit={handleSubmit(successfulSubmit)}
                            noValidate
                        >
                            <div className='fp-what-do-you-need-container'>
                                <RoundedButton
                                    className={
                                        reasonValue === REASON.FORGOT_USERNAME ? 'fp-options-lowlight' : EMPTY_STRING
                                    }
                                    onClick={(): void => setReasonValue(REASON.FORGOT_PASSWORD)}
                                    isOutlined
                                >
                                    {language.get(ForgotPasswordLanguage.ForgotPassword_WhatDoYouNeed_1)}
                                </RoundedButton>
                                <RoundedButton
                                    className={
                                        reasonValue === REASON.FORGOT_PASSWORD ? 'fp-options-lowlight' : EMPTY_STRING
                                    }
                                    onClick={(): void => setReasonValue(REASON.FORGOT_USERNAME)}
                                    isOutlined
                                >
                                    {language.get(ForgotPasswordLanguage.ForgotPassword_WhatDoYouNeed_2)}
                                </RoundedButton>
                            </div>

                            <ValidationErrors className='fp-email-input' name={EMAIL_FIELD_NAME}>
                                <OutlinedTextInput
                                    inputName={EMAIL_FIELD_NAME}
                                    labelLeft={language.get(
                                        ForgotPasswordLanguage.ForgotPassword_FieldName_EmailAddress
                                    )}
                                    rules={{
                                        required: language.get(
                                            ForgotPasswordLanguage.ForgotPassword_Validations_EmailAddress
                                        ),
                                        validate: {
                                            isEmailAddress: (email: string): ValidateResult => {
                                                if (!IsStringEmpty(email) && !email?.match(EMAIL_REGEX)) {
                                                    return language.get(
                                                        ForgotPasswordLanguage.ForgotPassword_Validations_EmailAddress_Invalid
                                                    )
                                                }

                                                return undefined
                                            }
                                        }
                                    }}
                                />
                            </ValidationErrors>

                            <ValidationErrors name={RECAPTCHA_FIELD_NAME} className='fp-recaptcha-validation-container'>
                                {reCaptchaHandle?.config?.enabled ? (
                                    <input
                                        type='hidden'
                                        name={RECAPTCHA_FIELD_NAME}
                                        ref={register({
                                            validate: {
                                                recaptchaRequired: (recaptcha: string): ValidateResult => {
                                                    if (IsStringEmpty(recaptcha)) {
                                                        return language.get(
                                                            ForgotPasswordLanguage.ForgotPassword_Validations_RecaptchaRequired
                                                        )
                                                    }

                                                    return undefined
                                                }
                                            }
                                        })}
                                    />
                                ) : (
                                    <></>
                                )}

                                <ReCaptcha
                                    setCaptchaHandle={setReCaptchaHandle}
                                    onChange={(recaptchaToken: OnlyNullable<string>): void => {
                                        setValue(RECAPTCHA_FIELD_NAME as keyof ForgotPasswordForm, recaptchaToken)
                                        trigger(RECAPTCHA_FIELD_NAME as keyof ForgotPasswordForm)
                                    }}
                                />
                            </ValidationErrors>

                            <FooterActions
                                submitHandler={(): void => {
                                    if (isReCaptchaInvisible) {
                                        reCaptchaHandle?.ref?.current?.executeAsync()?.then((): void => {
                                            handleSubmit(successfulSubmit)()
                                        })
                                    } else {
                                        handleSubmit(successfulSubmit)()
                                    }
                                }}
                                isReCaptchaEnabled={reCaptchaHandle?.config?.enabled ?? false}
                                isReCaptchaInvisible={isReCaptchaInvisible}
                            />
                        </form>
                    </FormProvider>
                </>
            )}
        </div>
    )
}
